import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canDelete, WRITE_PERMISSION_DENIED, DELETE_PERMISSION_DENIED } from '@/services/permissions';
import { getAgentSalespersonId } from '@/services/agent';
import { recordAudit } from '@/services/audit';
import { logInventoryChange } from '@/services/inventory';
import { getCompanySettings } from '@/services/finances';
import { defaultStatusForMethod } from '@/services/payments';
import { computeShippingCost, getShippingConfig } from '@/services/shipping';

export interface Sale {
  id: string;
  saleNumber: string;
  customerName: string;
  salespersonName: string;
  channel: string;
  total: number;
  costTotal: number;
  grossProfit: number;
  campaignName: string;
  saleDate: string;
  voided: boolean;
  hasOutOfStock: boolean;
  paymentMethod: string;
  paymentStatus: string;
  hasShipping: boolean;
}

export interface SalesResult {
  data: Sale[];
}

// Etiquetas legibles para los métodos de pago de las ventas directas.
export const SALE_PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Contra entrega (efectivo)',
  nequi_negocios: 'Nequi',
  transfer: 'Transferencia',
  pse: 'PSE',
  card_credit: 'Tarjeta de crédito',
  card_debit: 'Tarjeta débito',
  payment_link: 'ePayco',
  sistecredito: 'Sistecrédito',
};

export async function fetchSales(): Promise<SalesResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [salesRes, customersRes, salespersonsRes, itemsRes, paymentsRes] = await Promise.all([
      supabase.from('sales').select('*'),
      supabase.from('customers').select('id, first_name, last_name'),
      supabase.from('salespersons').select('id, full_name'),
      supabase.from('sale_items').select('sale_id').eq('out_of_stock', true),
      supabase.from('payments').select('sale_id, method, status, amount'),
    ]);

    if (salesRes.error) throw new Error(salesRes.error.message);
    if (customersRes.error) throw new Error(customersRes.error.message);
    if (salespersonsRes.error) throw new Error(salespersonsRes.error.message);
    if (itemsRes.error) throw new Error(itemsRes.error.message);
    if (paymentsRes.error) throw new Error(paymentsRes.error.message);

    const sales = (salesRes.data ?? []) as Array<Record<string, any>>;
    const outOfStockSaleIds = new Set(
      ((itemsRes.data ?? []) as Array<Record<string, any>>).map((i) => i.sale_id),
    );

    const paymentBySale = new Map<string, { method: string; approved: number; total: number }>();
    ((paymentsRes.data ?? []) as Array<Record<string, any>>).forEach((p) => {
      if (!p.sale_id) return;
      const entry = paymentBySale.get(p.sale_id) ?? { method: '', approved: 0, total: 0 };
      if (p.method) entry.method = p.method;
      entry.total += Number(p.amount ?? 0);
      if (p.status === 'approved') entry.approved += Number(p.amount ?? 0);
      paymentBySale.set(p.sale_id, entry);
    });

    // Filtro por agente: un agente de ventas solo ve las ventas que él facturó.
    const agentSalespersonId = await getAgentSalespersonId();
    const visibleSales = agentSalespersonId
      ? sales.filter((s) => s.salesperson_id === agentSalespersonId)
      : sales;

    const customerMap = new Map(
      ((customersRes.data ?? []) as Array<Record<string, any>>).map((c) => [c.id, c]),
    );
    const spMap = new Map(
      ((salespersonsRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s]),
    );

    const data = visibleSales
      .map((s) => {
        const customer = customerMap.get(s.customer_id);
        const sp = s.salesperson_id ? spMap.get(s.salesperson_id) : undefined;
        const pay = paymentBySale.get(s.id);
        return {
          id: s.id,
          saleNumber: s.sale_number,
          customerName: customer
            ? `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim()
            : '',
          salespersonName: sp?.full_name ?? '',
          channel: s.order_id ? 'ecommerce' : 'direct',
          total: Number(s.total),
          costTotal: Number(s.cost_total),
          grossProfit: Number(s.gross_profit),
          campaignName: '',
          saleDate: s.sale_date,
          voided: Boolean(s.voided_at),
          hasOutOfStock: outOfStockSaleIds.has(s.id),
          paymentMethod: pay?.method ?? '',
          paymentStatus: pay && pay.total > 0 ? (pay.approved >= pay.total ? 'approved' : 'pending') : '',
          // Una venta "requiere despacho" si guarda los datos del destinatario
          // (notas __shipping) o si tiene flete cobrado.
          hasShipping:
            String(s.notes ?? '').includes('__shipping') || Number(s.shipping_cost ?? 0) > 0,
        };
      })
      .sort((a, b) => (a.saleDate < b.saleDate ? 1 : -1));

    return { data };
  } catch (err) {
    console.error('[sales] Supabase connection failed:', err);
    throw err;
  }
}

// Item de venta directa (variante + cantidad + precios).
export interface DirectSaleItemInput {
  variantId: string;
  quantity: number;
  unitPrice: number;
  unitCost: number;
}

// Datos del destinatario de una venta directa con envío (guía media carta).
export interface DirectSaleShippingInput {
  recipientName: string;
  recipientPhone: string;
  recipientIdNumber: string;
  recipientEmail: string;
  department: string;
  city: string;
  address: string;
  notes: string;
}

// Entrada de venta directa (sin pedido previo).
export interface DirectSaleInput {
  customerId: string;
  paymentMethod: string;
  items: DirectSaleItemInput[];
  salespersonId?: string;
  shipping?: DirectSaleShippingInput;
  // Valor de envío editado manualmente en el formulario. Si se define, tiene
  // prioridad sobre el cálculo automático por peso/ciudad.
  shippingCostOverride?: number;
}

export interface DirectSaleResult {
  success: boolean;
  saleId?: string;
  saleNumber?: string;
  error?: string;
}

interface ProductionOrderParams {
  customerId: string;
  salespersonId?: string;
  paymentMethod: string;
  saleNumber: string;
  saleId: string;
  items: DirectSaleItemInput[];
}

// Crea un pedido de producción (orders con production=true) a partir de los
// ítems facturados sin stock en una venta directa. Cada ítem trae como
// `quantity` solo la cantidad que falta (facturado − disponible). Nace en
// estado "para_produccion" para aparecer en el módulo de Producción.
async function createProductionOrderForSale(params: ProductionOrderParams): Promise<void> {
  if (!supabase) return;

  try {
    const subtotal = params.items.reduce((sum, it) => sum + it.quantity * it.unitPrice, 0);

    // Número secuencial de producción (prefijo PCP-P-), igual que el checkout.
    const { data: lastProd } = await supabase
      .from('orders')
      .select('order_number')
      .eq('production', true)
      .order('created_at', { ascending: false })
      .limit(1);
    let nextNum = 101;
    if (lastProd && lastProd.length > 0) {
      const m = String(lastProd[0].order_number).match(/(\d+)/);
      if (m) nextNum = parseInt(m[1], 10) + 1;
    }
    const orderNumber = `PCP-P-0${nextNum}`;

    const { data: order, error } = await supabase
      .from('orders')
      .insert({
        order_number: orderNumber,
        customer_id: params.customerId,
        salesperson_id: params.salespersonId ?? null,
        channel: 'direct',
        status: 'para_produccion',
        production: true,
        sale_id: params.saleId,
        payment_method: params.paymentMethod,
        subtotal,
        discount_amount: 0,
        shipping_cost: 0,
        total: subtotal,
        notes: `Producción por venta directa ${params.saleNumber}`,
      })
      .select('id');

    if (error || !order?.[0]) {
      console.warn('[sales] production order insert failed:', error?.message);
      return;
    }

    const orderId = (order[0] as { id: string }).id;
    const items = params.items.map((it) => ({
      order_id: orderId,
      variant_id: it.variantId,
      quantity: it.quantity,
      unit_price: it.unitPrice,
      unit_cost: it.unitCost,
      discount: 0,
      subtotal: it.quantity * it.unitPrice,
    }));
    const { error: itemsError } = await supabase.from('order_items').insert(items);
    if (itemsError) console.warn('[sales] production order_items insert failed:', itemsError.message);

    // Refleja el pago en el pedido de producción: contra entrega, transferencia
    // y Nequi quedan pendientes; tarjeta, PSE y pago en línea quedan pagados.
    const paymentStatus = defaultStatusForMethod(params.paymentMethod);
    const { error: payError } = await supabase.from('payments').insert({
      payment_number: `P-${Date.now().toString().slice(-6)}`,
      order_id: orderId,
      method: params.paymentMethod,
      status: paymentStatus,
      amount: subtotal,
      transaction_ref: null,
    });
    if (payError) console.warn('[sales] production payments insert failed:', payError.message);
  } catch (err) {
    console.warn('[sales] create production order failed:', err);
  }
}

// Crea una venta directa: inserta sales + sale_items, descuenta inventario y
// registra el movimiento de trazabilidad (SALE). Mejor esfuerzo, no atómico.
export async function createDirectSale(input: DirectSaleInput): Promise<DirectSaleResult> {
  if (!(await canWrite('sales'))) return { success: false, error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, error: 'Supabase no está configurado.' };
  }

  const subtotal = input.items.reduce((sum, it) => sum + it.quantity * it.unitPrice, 0);
  const costTotal = input.items.reduce((sum, it) => sum + it.quantity * it.unitCost, 0);
  const grossProfit = subtotal - costTotal;
  // Numeración de venta UNIFICADA con el canal en línea (PCP-V-0001) mediante
  // el generador compartido en la base de datos. Si el RPC fallara, se usa un
  // número de respaldo para no bloquear la venta.
  let saleNumber = `PCP-V-${Date.now().toString().slice(-6)}`;
  try {
    const { data: saleNumberData, error: saleNumberErr } = await supabase.rpc('next_sale_number');
    if (!saleNumberErr && typeof saleNumberData === 'string' && saleNumberData) {
      saleNumber = saleNumberData;
    }
  } catch (numErr) {
    console.warn('[sales] next_sale_number failed, usando respaldo:', numErr);
  }
  // Flete de Interrapidísimo: respeta el valor editado manualmente en el
  // formulario; si no, se calcula por ciudad (0 si no hay envío).
  const shippingCost = input.shipping
    ? (typeof input.shippingCostOverride === 'number'
        ? input.shippingCostOverride
        : computeShippingCost(input.shipping.city, await getShippingConfig()))
    : 0;
  const total = subtotal + shippingCost;
  const settings = await getCompanySettings();
  const donationAmount = Math.round(subtotal * (settings.donationPercent / 100) * 100) / 100;

  try {
    const { data, error } = await supabase
      .from('sales')
      .insert({
        sale_number: saleNumber,
        customer_id: input.customerId,
        salesperson_id: input.salespersonId ?? null,
        status: 'completed',
        sale_date: new Date().toISOString(),
        subtotal,
        discount_amount: 0,
        shipping_cost: shippingCost,
        total,
        cost_total: costTotal,
        gross_profit: grossProfit,
        donation_pct: settings.donationPercent,
        donation_amount: donationAmount,
      })
      .select('id');

    if (error) return { success: false, error: error.message };
    const sale = data?.[0] as { id: string } | undefined;
    if (!sale) return { success: false, error: 'No se pudo crear la venta.' };

    // Guarda el destinatario del envío para poder emitir la guía media carta.
    // Es best-effort: si falla, la venta ya quedó registrada con su total correcto.
    if (input.shipping) {
      try {
        const { error: shipErr } = await supabase
          .from('sales')
          .update({ notes: JSON.stringify({ __shipping: input.shipping }) })
          .eq('id', sale.id);
        if (shipErr) console.warn('[sales] shipping note persist failed:', shipErr.message);
      } catch (shipErr) {
        console.warn('[sales] shipping note persist exception:', shipErr);
      }
    }

    // Determina qué items se facturan sin stock (cantidad > disponible) y la
    // cantidad exacta que falta, para marcarlos y mandar a producción el faltante.
    const availableByVariant = new Map<string, number>();
    const uniqueVariantIds = Array.from(new Set(input.items.map((it) => it.variantId)));
    for (const vid of uniqueVariantIds) {
      const { data: invRow } = await supabase
        .from('inventory')
        .select('available')
        .eq('variant_id', vid)
        .maybeSingle();
      availableByVariant.set(vid, Number(invRow?.available ?? 0));
    }

    const saleItems = input.items.map((it) => {
      const available = availableByVariant.get(it.variantId) ?? 0;
      return {
        sale_id: sale.id,
        variant_id: it.variantId,
        quantity: it.quantity,
        unit_price: it.unitPrice,
        unit_cost: it.unitCost,
        discount: 0,
        subtotal: it.quantity * it.unitPrice,
        profit: (it.unitPrice - it.unitCost) * it.quantity,
        out_of_stock: it.quantity > available,
      };
    });
    const { error: itemsError } = await supabase.from('sale_items').insert(saleItems);
    if (itemsError) console.warn('[sales] sale_items insert failed:', itemsError.message);

    // Registra el pago de la venta directa. Contra entrega, transferencia y
    // Nequi quedan pendientes; tarjeta, PSE y pago en línea se aprueban al momento.
    const paymentStatus = defaultStatusForMethod(input.paymentMethod);
    const { error: payError } = await supabase.from('payments').insert({
      payment_number: `P-${Date.now().toString().slice(-6)}`,
      sale_id: sale.id,
      method: input.paymentMethod,
      status: paymentStatus,
      amount: total,
      transaction_ref: null,
    });
    if (payError) console.warn('[sales] payments insert failed:', payError.message);

    // Registra la donación (total × % global de Configuración), igual que el
    // resto de canales, para que el fondo de donaciones refleje todas las ventas.
    try {
      const { error: donErr } = await supabase.rpc('register_sale_donation', {
        p_sale_total: subtotal,
        p_note: `Venta directa ${saleNumber}`,
      });
      if (donErr) console.warn('[sales] donation register failed:', donErr.message);
    } catch (donErr) {
      console.warn('[sales] donation register exception:', donErr);
    }

    // Ítems que faltan: manda a producción solo la cantidad que supera el stock.
    const productionItems = input.items
      .filter((it) => it.quantity > (availableByVariant.get(it.variantId) ?? 0))
      .map((it) => ({
        variantId: it.variantId,
        quantity: it.quantity - (availableByVariant.get(it.variantId) ?? 0),
        unitPrice: it.unitPrice,
        unitCost: it.unitCost,
      }));
    if (productionItems.length > 0) {
      await createProductionOrderForSale({
        customerId: input.customerId,
        salespersonId: input.salespersonId,
        paymentMethod: input.paymentMethod,
        saleNumber,
        saleId: sale.id,
        items: productionItems,
      });
    }

    recordAudit({
      entityType: 'sales',
      entityId: sale.id,
      action: 'CREATE',
      newValue: saleNumber,
    });

    // Comisión automática: si la venta tiene vendedor, se genera sobre la
    // ganancia (valor vendido − costo) con el % individual del vendedor, o el
    // % general de Configuración si no tiene uno propio.
    if (input.salespersonId) {
      try {
        const { data: spRow } = await supabase
          .from('salespersons')
          .select('commission_rate')
          .eq('id', input.salespersonId)
          .maybeSingle();
        const spRate = Number((spRow as Record<string, any> | null)?.commission_rate ?? 0);
        const rate = spRate > 0 ? spRate : settings.sellerCommissionPercent;
        const commissionAmount = grossProfit * (rate / 100);
        if (commissionAmount > 0) {
          const { error: commError } = await supabase.from('commissions').insert({
            salesperson_id: input.salespersonId,
            sale_id: sale.id,
            sale_amount: grossProfit,
            commission_rate: rate,
            commission_amount: commissionAmount,
            is_paid: false,
          });
          if (commError) console.warn('[sales] commission insert failed:', commError.message);
        }
      } catch (commErr) {
        console.warn('[sales] commission auto-create failed:', commErr);
      }
    }

    for (const it of input.items) {
      const { data: inv } = await supabase
        .from('inventory')
        .select('available, reserved, consigned')
        .eq('variant_id', it.variantId)
        .maybeSingle();
      const prev = Number(inv?.available ?? 0);
      const reserved = Number(inv?.reserved ?? 0);
      const consigned = Number(inv?.consigned ?? 0);
      const next = Math.max(0, prev - it.quantity);

      await supabase.from('inventory').update({ available: next }).eq('variant_id', it.variantId);

      await logInventoryChange(it.variantId, [
        { fieldName: 'available', oldValue: prev, newValue: next },
      ]);

      await supabase.from('inventory_movements').insert({
        variant_id: it.variantId,
        movement_type: 'SALE',
        quantity: next - prev,
        prev_available: prev,
        new_available: next,
        prev_reserved: reserved,
        new_reserved: reserved,
        prev_consigned: consigned,
        new_consigned: consigned,
        unit_cost: it.unitCost,
        reason: `Venta directa - ${saleNumber}`,
      });
    }

    return { success: true, saleId: sale.id, saleNumber };
  } catch (err) {
    console.warn('[sales] createDirectSale failed:', err);
    return { success: false, error: 'Error de conexión al crear la venta.' };
  }
}

export interface SaleItemDetail {
  productName: string;
  sizeName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

export interface SalePaymentDetail {
  id: string;
  method: string;
  status: string;
  amount: number;
}

export interface SaleDetail {
  id: string;
  saleNumber: string;
  customerName: string;
  salespersonName: string;
  channel: string;
  saleDate: string;
  total: number;
  costTotal: number;
  grossProfit: number;
  voided: boolean;
  voidReason: string;
  items: SaleItemDetail[];
  payments: SalePaymentDetail[];
  paymentMethod: string;
  paymentStatus: string;
  productionOrderId: string;
  productionOrderNumber: string;
  shippingCost: number;
  shipping: DirectSaleShippingInput | null;
}

// Consulta el detalle completo de una venta: qué se vendió (producto + talla),
// quién (cliente + vendedor) y por qué medio (canal + pagos).
export async function fetchSaleDetail(saleId: string): Promise<SaleDetail | null> {
  if (!isSupabaseConfigured || !supabase) return null;

  try {
    const [
      saleRes,
      itemsRes,
      paymentsRes,
      variantsRes,
      productsRes,
      sizesRes,
      customersRes,
      salespersonsRes,
    ] = await Promise.all([
      supabase.from('sales').select('*').eq('id', saleId).maybeSingle(),
      supabase.from('sale_items').select('*').eq('sale_id', saleId),
      supabase.from('payments').select('id, method, status, amount').eq('sale_id', saleId),
      supabase.from('product_variants').select('id, product_id, size_id, sku'),
      supabase.from('products').select('id, name'),
      supabase.from('sizes').select('id, name'),
      supabase.from('customers').select('id, first_name, last_name'),
      supabase.from('salespersons').select('id, full_name'),
    ]);

    const sale = saleRes.data as Record<string, any> | null;
    if (!sale) return null;

    // Recupera el destinatario del envío guardado en las notas de la venta.
    let parsedShipping: DirectSaleShippingInput | null = null;
    try {
      const rawNotes = String(sale.notes ?? '');
      if (rawNotes.includes('__shipping')) {
        const parsed = JSON.parse(rawNotes) as { __shipping?: DirectSaleShippingInput };
        if (parsed && parsed.__shipping) parsedShipping = parsed.__shipping;
      }
    } catch {
      parsedShipping = null;
    }

    // Pedido de producción vinculado a esta venta directa (productos sin stock).
    const { data: prodOrder } = await supabase
      .from('orders')
      .select('id, order_number')
      .eq('sale_id', saleId)
      .eq('production', true)
      .maybeSingle();

    const productMap = new Map(
      ((productsRes.data ?? []) as Array<Record<string, any>>).map((p) => [p.id, p.name]),
    );
    const sizeMap = new Map(
      ((sizesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.name]),
    );
    const variantMap = new Map(
      ((variantsRes.data ?? []) as Array<Record<string, any>>).map((v) => [v.id, v]),
    );

    const items: SaleItemDetail[] = ((itemsRes.data ?? []) as Array<Record<string, any>>).map(
      (it) => {
        const variant = variantMap.get(it.variant_id);
        return {
          productName: variant ? productMap.get(variant.product_id) ?? '' : '',
          sizeName: variant ? sizeMap.get(variant.size_id) ?? '' : '',
          sku: variant?.sku ?? '',
          quantity: Number(it.quantity),
          unitPrice: Number(it.unit_price),
          subtotal: Number(it.subtotal),
        };
      },
    );

    const payments: SalePaymentDetail[] = ((paymentsRes.data ?? []) as Array<
      Record<string, any>
    >).map((p) => ({
      id: p.id,
      method: p.method ?? '',
      status: p.status ?? '',
      amount: Number(p.amount),
    }));

    const approvedAmount = payments.reduce(
      (sum, p) => sum + (p.status === 'approved' ? p.amount : 0),
      0,
    );
    const paymentMethod = payments.map((p) => p.method).filter(Boolean)[0] ?? '';
    const paymentStatus = payments.length > 0
      ? (approvedAmount >= Number(sale.total) ? 'approved' : 'pending')
      : '';

    const customer = ((customersRes.data ?? []) as Array<Record<string, any>>).find(
      (c) => c.id === sale.customer_id,
    );
    const salesperson = ((salespersonsRes.data ?? []) as Array<Record<string, any>>).find(
      (s) => s.id === sale.salesperson_id,
    );

    return {
      id: sale.id,
      saleNumber: sale.sale_number,
      customerName: customer ? `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim() : '',
      salespersonName: salesperson?.full_name ?? '',
      channel: sale.order_id ? 'ecommerce' : 'direct',
      saleDate: sale.sale_date,
      total: Number(sale.total),
      costTotal: Number(sale.cost_total),
      grossProfit: Number(sale.gross_profit),
      voided: Boolean(sale.voided_at),
      voidReason: sale.void_reason ?? '',
      items,
      payments,
      paymentMethod,
      paymentStatus,
      productionOrderId: (prodOrder as Record<string, any> | null)?.id ?? '',
      productionOrderNumber: (prodOrder as Record<string, any> | null)?.order_number ?? '',
      shippingCost: Number(sale.shipping_cost ?? 0),
      shipping: parsedShipping,
    };
  } catch (err) {
    console.error('[sales] fetch detail failed:', err);
    throw err;
  }
}

// Marca como pagada una venta que estaba pendiente (p. ej. contra entrega):
// actualiza el pago de la venta a "approved" y, si existe un pedido de
// producción asociado (venta con productos sin stock), sincroniza su pago.
export async function markSaleAsPaid(saleId: string): Promise<{ error: string | null }> {
  if (!(await canWrite('payments'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  try {
    const { data: sale } = await supabase
      .from('sales')
      .select('sale_number')
      .eq('id', saleId)
      .maybeSingle();
    if (!sale) return { error: 'No se encontró la venta.' };
    const saleNumber = (sale as Record<string, any>).sale_number ?? '';

    const toApprove: string[] = [];

    // 1) Pagos de la venta directa.
    const { data: salePayments } = await supabase
      .from('payments')
      .select('id, status')
      .eq('sale_id', saleId);
    ((salePayments ?? []) as Array<Record<string, any>>).forEach((p) => {
      if (p.status !== 'approved') toApprove.push(p.id);
    });

    // 2) Pagos del pedido de producción asociado (si hubo productos sin stock).
    if (saleNumber) {
      const { data: prodOrders } = await supabase
        .from('orders')
        .select('id')
        .eq('production', true)
        .like('notes', `%${saleNumber}%`);
      for (const o of (prodOrders ?? []) as Array<Record<string, any>>) {
        const { data: prodPayments } = await supabase
          .from('payments')
          .select('id, status')
          .eq('order_id', o.id);
        ((prodPayments ?? []) as Array<Record<string, any>>).forEach((p) => {
          if (p.status !== 'approved') toApprove.push(p.id);
        });
      }
    }

    for (const pid of toApprove) {
      const { data: current } = await supabase
        .from('payments')
        .select('status')
        .eq('id', pid)
        .maybeSingle();
      const { error } = await supabase.from('payments').update({ status: 'approved' }).eq('id', pid);
      if (error) console.warn('[sales] markSaleAsPaid update failed:', error.message);
      recordAudit({
        entityType: 'payments',
        entityId: pid,
        action: 'UPDATE',
        fieldName: 'status',
        oldValue: (current as Record<string, any> | null)?.status ?? '',
        newValue: 'approved',
      });
    }

    return { error: null };
  } catch (err) {
    console.warn('[sales] markSaleAsPaid failed:', err);
    return { error: 'Error de conexión al marcar la venta como pagada.' };
  }
}

export interface VoidSaleResult {
  success: boolean;
  error?: string;
}

// Anula una venta: revierte el stock, elimina la comisión asociada y la marca
// como anulada (voided_at) para excluirla de los registros contables. Se usa
// cuando un cliente se retracta (p. ej. contra entrega) y la factura no debe
// computar. Solo el administrador puede anular.
export async function voidSale(saleId: string, reason?: string): Promise<VoidSaleResult> {
  if (!(await canDelete('sales'))) return { success: false, error: DELETE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, error: 'Supabase no está configurado.' };
  }

  try {
    const { data: sale } = await supabase
      .from('sales')
      .select('sale_number, voided_at')
      .eq('id', saleId)
      .maybeSingle();
    if (!sale) return { success: false, error: 'No se encontró la venta.' };
    if ((sale as Record<string, any>).voided_at) {
      return { success: false, error: 'La venta ya está anulada.' };
    }

    const { data: items } = await supabase
      .from('sale_items')
      .select('variant_id, quantity, unit_cost')
      .eq('sale_id', saleId);
    const itemRows = (items ?? []) as Array<Record<string, any>>;

    for (const it of itemRows) {
      const { data: inv } = await supabase
        .from('inventory')
        .select('available, reserved, consigned')
        .eq('variant_id', it.variant_id)
        .maybeSingle();
      const prev = Number(inv?.available ?? 0);
      const reserved = Number(inv?.reserved ?? 0);
      const consigned = Number(inv?.consigned ?? 0);
      const next = prev + Number(it.quantity);

      await supabase.from('inventory').update({ available: next }).eq('variant_id', it.variant_id);

      await supabase.from('inventory_movements').insert({
        variant_id: it.variant_id,
        movement_type: 'SALE_VOID',
        quantity: Number(it.quantity),
        prev_available: prev,
        new_available: next,
        prev_reserved: reserved,
        new_reserved: reserved,
        prev_consigned: consigned,
        new_consigned: consigned,
        unit_cost: Number(it.unit_cost),
        reason: `Anulación de venta - ${(sale as Record<string, any>).sale_number}`,
      });
    }

    // Revertir la comisión asociada (si existe).
    await supabase.from('commissions').delete().eq('sale_id', saleId);

    // Revertir la donación asociada (fondo acumulado + entrada individual),
    // igual que la comisión, para que la anulación también descuente el aporte.
    try {
      const { error: donErr } = await supabase.rpc('void_sale_donation', { p_sale_id: saleId });
      if (donErr) console.warn('[sales] donation revert failed:', donErr.message);
    } catch (donErr) {
      console.warn('[sales] donation revert exception:', donErr);
    }

    const { error: voidErr } = await supabase
      .from('sales')
      .update({ voided_at: new Date().toISOString(), void_reason: reason?.trim() || null })
      .eq('id', saleId);
    if (voidErr) return { success: false, error: voidErr.message };

    recordAudit({
      entityType: 'sales',
      entityId: saleId,
      action: 'UPDATE',
      fieldName: 'voided',
      oldValue: 'false',
      newValue: 'true',
    });

    return { success: true };
  } catch (err) {
    console.warn('[sales] voidSale failed:', err);
    return { success: false, error: 'Error de conexión al anular la venta.' };
  }
}